/**
 * The confirmation that sits between a press and the brake actually moving
 * (#1892).
 *
 * BOTH directions are confirmed, for different reasons:
 *
 *   - OPENING is the press that lets the next pass mail real customers about
 *     their own money, and a sent mail cannot be recalled. The dialog is
 *     danger-toned and leads with that consequence; the reassurance ("nothing
 *     is sent by this press itself") comes second, because read first it means
 *     "this is safe to press", which is the exact misreading that produces an
 *     unwanted first send.
 *   - CLOSING is cheap to confirm and buys the `reason` field, which is what
 *     makes the resulting state self-describing. "Off" and "off because the
 *     03:00 pass mailed forty people wrongly" call for opposite responses from
 *     whoever finds the page next, and only the second one is a handover.
 */
import type { ReactNode } from 'react';
import { useT } from '../../i18n/index.ts';
import { ConfirmDialog } from '../../ui/index.ts';

interface Props {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly enabling: boolean;
  readonly loading: boolean;
  readonly reasonField: ReactNode;
  readonly onConfirm: () => void;
}

export function BalanceAlertSwitchDialog({
  open,
  onOpenChange,
  enabling,
  loading,
  reasonField,
  onConfirm,
}: Props): JSX.Element {
  const t = useT();
  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      tone={enabling ? 'danger' : 'default'}
      title={t(enabling ? 'balance_alerts.switch.enable_title' : 'balance_alerts.switch.disable_title')}
      body={
        <div className="flex flex-col gap-3">
          <p
            className={
              enabling
                ? 'm-0 rounded border border-bad-line bg-bad-dim/50 px-2.5 py-2 text-dense text-bad-bright'
                : 'm-0 rounded border border-warn-line bg-warn-dim/50 px-2.5 py-2 text-dense text-warn'
            }
            data-testid="balance-alerts-switch-confirm-consequence"
          >
            {t(
              enabling
                ? 'balance_alerts.switch.enable_consequence'
                : 'balance_alerts.switch.disable_consequence',
            )}
          </p>
          <p className="m-0">
            {t(
              enabling
                ? 'balance_alerts.switch.enable_detail'
                : 'balance_alerts.switch.disable_detail',
            )}
          </p>
          {reasonField}
        </div>
      }
      confirmLabel={t(
        enabling ? 'balance_alerts.switch.enable_action' : 'balance_alerts.switch.disable_action',
      )}
      cancelLabel={t('common.cancel')}
      closeLabel={t('common.close')}
      loading={loading}
      onConfirm={onConfirm}
      testId="balance-alerts-switch-confirm"
    />
  );
}
